/**
 * Voyado Elevate: autocomplete.
 *
 * Binds to every search field in the theme and replaces Klevu quicksearch.
 *
 * Why JS creates its own DOM instead of putting the markup in Liquid: the theme
 * has four header variants (`snippets/header-{center-search,top-menu,
 * without-menu,bottom-menu}.liquid`) that all use the same input ID. The active
 * variant is controlled by `header_type` in the theme editor, and
 * `sections/header-group.json` shows up in the auto-sync commits. Creating the
 * results panel as a sibling of the input means we do not have to patch four
 * snippets, and a variant switch in the theme editor cannot silently kill
 * autocomplete.
 *
 * Same reason the a11y attributes are set here: the markup in
 * `header-center-search.liquid:236-247` has `role="combobox"`, `aria-expanded`
 * and `aria-autocomplete` inside `{% if settings.predictive_search_enabled %}`,
 * which is `false`. The live input therefore has none of them. We cannot turn
 * the setting on to get them back, because that would also activate the theme's
 * own predictive search, leaving two autocompletes fighting over one field.
 *
 * See docs/voyado-elevate-implementering.md sections 6 and 12.
 */

(function () {
  'use strict';

  const E = window.CGElevate;

  const INPUT_IDS = ['Search-In-Modal-1', 'Search-In-Template'];
  const DEBOUNCE_MS = 200;

  /*
   * Almost no counts in this file. Every number that decides how much the panel
   * shows, per touchpoint, is a merchandiser setting under Auto-complete &
   * Search settings in the Experience app: phrase suggestions, product
   * suggestions, recent searches and recently viewed. A cap here would silently
   * override the dashboard, so a change made there would appear to do nothing.
   * The panel renders whatever the response contains, and requests for these
   * are sent without a limit so Elevate's own default applies.
   *
   * The one exception is the category/page content list - see
   * contentListsConfig() below. Its "Content suggestions" toggle in that same
   * Experience settings page governs a different, currently-empty field
   * (contentSuggestions), not the content list this theme actually uses, so
   * there is no dashboard setting to defer to here.
   */
  const RECENT_KEY = 'cg_elevate_recent_searches';

  /*
   * Bound on the local fallback list only, and a technical one: it is not an
   * Elevate surface, so no dashboard setting governs it, and something has to
   * stop an unbounded list from filling the sidebar and localStorage. Elevate's
   * own recent searches are never trimmed by it, see recentTerms().
   *
   * Deriving this from the length Elevate returned was tried and reverted: a
   * returned length is a floor, not the setting. A visitor with two searches
   * would pin the fallback at two permanently, including in storage.
   */
  const LOCAL_RECENT_BOUND = 10;

  // Id of the Elevate content list that carries categories and pages. Must match
  // a list configured in the Experience app. Requesting an unknown id returns an
  // empty list rather than an error, so this is safe before it is set up.
  const CONTENT_LIST_ID = 'content';

  // Set from sections/header.liquid's own settings, since that is where the
  // search field lives. Merchandiser-owned: which recommendation list (and
  // optionally which Elevate page) fills the empty state, see
  // startPageRecommendations below.
  function autocompleteConfig() {
    return window.CGElevateAutocompleteConfig || {};
  }

  /**
   * One combined limit for categories and pages together, not a separate
   * one per type: Elevate's API rejects requesting the same list id twice
   * ("duplicate id"), confirmed empirically 2026-09-24 when this tried
   * `contentFilter` on two entries against the same list to split them - so
   * a second, type-filtered entry is not an option without a second network
   * round trip, which is not worth it here. Elevate mixes and ranks both
   * types under the one list itself.
   *
   * Without an explicit limit, Elevate defaults to an undocumented, small
   * per-page-type count (3 for autocomplete) - also confirmed empirically
   * the same day, and unrelated to the "Content suggestions" toggle in
   * Experience (that governs a separate, currently empty field,
   * contentSuggestions). A limit of 0 (or unset) skips the list entirely,
   * rather than sending a request that would just come back empty.
   */
  function contentListsConfig() {
    const limit = autocompleteConfig().contentSuggestionsLimit;
    return limit > 0 ? [{ id: CONTENT_LIST_ID, limit: limit }] : [];
  }

  // Monotonic sequence instead of AbortController. The SDK cannot abort calls,
  // so without this a slow response for "gla" could overwrite a fast response
  // for "glass".
  let seq = 0;

  /**
   * The quiet period after a render before native cards are fetched for it.
   * Long enough to sit out the gap between two keystrokes, short enough that a
   * visitor who has stopped typing does not notice waiting for it.
   */
  const UPGRADE_DELAY_MS = 350;
  let upgradeTimer = null;

  /**
   * Defers the native-card swap for a freshly rendered panel, and cancels the
   * previous render's swap if it has not started yet. See the call in open()
   * for why: each card is one Liquid render, so upgrading a result set the
   * visitor has already typed past costs a round trip per product and buys
   * nothing.
   *
   * `seq` is the same monotonic counter the queries use, captured here and
   * rechecked when the timer fires, so a render that has been superseded in the
   * meantime upgrades nothing even if its timer survives.
   */
  function scheduleUpgrade(container) {
    clearTimeout(upgradeTimer);
    if (!window.CGElevateNativeCards) return;
    const current = seq;
    upgradeTimer = setTimeout(() => {
      if (current !== seq) return;
      window.CGElevateNativeCards.upgradeLazily(container, {
        placeholderSelector: '.elevate-ac__product',
        linkSelector: '.elevate-ac__product-link'
      });
    }, UPGRADE_DELAY_MS);
  }

  /* ------------------------------------------------------------ recent searches */

  /**
   * Elevate's own recent searches take precedence over the local list.
   *
   * Elevate keeps them per customerKey, so they follow the visitor across
   * devices, and the count is a merchandiser setting per touchpoint in the
   * Experience app. The local list is the fallback for the cases Elevate cannot
   * cover: the SDK blocked by consent, or a session that has not sent a search
   * notification yet.
   *
   * The response items are objects with a `q`, same as phraseSuggestions, but
   * plain strings are accepted too so a shape change does not empty the section.
   */
  function recentTerms(elevateList) {
    const remote = (Array.isArray(elevateList) ? elevateList : [])
      .map((item) => (typeof item === 'string' ? item : (item && item.q) || ''))
      .filter(Boolean);
    return remote.length ? remote : readRecent();
  }

  function readRecent() {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((s) => typeof s === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  function pushRecent(term) {
    const q = String(term || '').trim();
    if (!q) return;
    try {
      // Case-insensitive, because the same search arrives both as the visitor's
      // own typing ("Glass") and as Elevate's lowercased phrase ("glass"). An
      // exact comparison let both into the list, and since the list renders with
      // a capitalised first letter they then looked like one entry twice.
      const lower = q.toLowerCase();
      const next = [q, ...readRecent().filter((s) => s.toLowerCase() !== lower)].slice(
        0,
        LOCAL_RECENT_BOUND
      );
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (e) {
      // localStorage may be unavailable. Recent searches are a bonus, not a must.
    }
  }

  /* ------------------------------------------------------- recently viewed products */

  const RECENTLY_VIEWED_KEY = 'cg_elevate_recently_viewed_products';

  /**
   * Local fallback for the empty-state "recently viewed" section, same
   * reasoning as the recent-searches fallback above: Elevate's own
   * `recentlyViewed` needs a persisted customerKey, which requires consent,
   * so a declining visitor gets nothing there even after genuinely viewing
   * products this session. Recorded regardless of consent, same as recent
   * searches - this never leaves the browser (no network call, nothing sent
   * to Elevate or anywhere else), so it carries none of the cross-session,
   * shared-profile concerns actual tracking would; it is closer to the
   * browser's own history than to analytics. collectPopular() below only
   * reads it when Elevate's own list came back empty.
   *
   * window.CGElevateCurrentProduct is set by layout/theme.liquid on product
   * pages only - see there for the shape.
   */
  function readRecentlyViewedProducts() {
    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function pushRecentlyViewedProduct(product) {
    if (!product || !product.key) return;
    try {
      const next = [product, ...readRecentlyViewedProducts().filter((p) => p.key !== product.key)].slice(
        0,
        LOCAL_RECENT_BOUND
      );
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
    } catch (e) {
      // localStorage may be unavailable. Recently viewed is a bonus, not a must.
    }
  }

  if (window.CGElevateCurrentProduct) {
    pushRecentlyViewedProduct(window.CGElevateCurrentProduct);
  }

  /**
   * Shapes the local list into the same { ticket, products: [product] } form
   * flattenGroups() produces from an Elevate response, so renderProducts()
   * needs no separate code path. No ticket: Elevate never saw these views,
   * so there is nothing to notify a click against - productItem() already
   * renders an empty data-voyado-ticket safely when there is none.
   */
  function recentlyViewedProductGroups() {
    return readRecentlyViewedProducts().map((product) => ({
      ticket: '',
      products: [
        {
          key: product.key,
          title: product.title,
          brand: product.brand,
          link: product.link,
          imageInfo: product.image ? { thumbnail: product.image } : {},
          // Shaped the way Elevate shapes it, so productItem() finds the
          // variant key in the same place either way and the card gets its
          // member price - see assets/elevate-member-price.js.
          variants: product.variantKey ? [{ key: product.variantKey }] : [],
          sellingPrice: { min: product.price, max: product.price },
          listPrice: product.compareAtPrice ? { min: product.compareAtPrice } : {}
        }
      ]
    }));
  }

  /* -------------------------------------------------------------------- render */

  /**
   * Elevate marks the matched part of a phrase with curly braces:
   * `{glass}beholdere`. Escaping has to happen inside the callback so that
   * `<mark>` survives while the content is escaped.
   */
  function highlightPhrase(item) {
    const highlighted = item && item.highlighted;
    const plain = (item && item.q) || '';
    if (!highlighted) return E.esc(plain);
    return String(highlighted).replace(/\{([^}]+)\}|([^{]+)/g, (_, marked, rest) =>
      marked !== undefined ? '<mark>' + E.esc(marked) + '</mark>' : E.esc(rest)
    );
  }

  function searchUrl(term, origin) {
    const params = new URLSearchParams({ q: term, type: 'product' });
    if (origin) params.set('origin', origin);
    return '/search?' + params.toString();
  }

  function productImage(product, index) {
    const info = product.imageInfo || {};
    // `thumbnail` is the right source for small slots. The reference
    // implementation never used it and loaded full-size images into 80x80 slots.
    const raw = info.thumbnail || (info.images && info.images[0] && info.images[0].sources && info.images[0].sources[0] && info.images[0].sources[0].url);
    const url = E.safeUrl(raw);
    if (!url) return '';
    const alt = (info.images && info.images[0] && info.images[0].alt) || product.title || '';
    return (
      '<img class="elevate-ac__img" src="' +
      E.esc(url) +
      '" alt="' +
      E.esc(alt) +
      '" width="64" height="64" loading="' +
      (index < 2 ? 'eager' : 'lazy') +
      '" decoding="async">'
    );
  }

  /**
   * Mirrors snippets/price.liquid's markup rather than inventing its own, so
   * component-price.css and custom.css size and colour it - see productItem().
   * `.elevate-ac__price` on the amount stays as the hook
   * assets/elevate-member-price.js looks for.
   */
  function productPrice(product) {
    const selling = E.priceRange(product.sellingPrice);
    if (!selling) return '';
    const list = product.listPrice || {};
    const sp = product.sellingPrice && product.sellingPrice.min;
    const onSale =
      list.min !== null && list.min !== undefined && sp !== null && sp !== undefined && Number(list.min) > Number(sp);
    return (
      '<div class="price collection' + (onSale ? ' price--on-sale' : '') + '">' +
      '<dl>' +
      '<div class="' + (onSale ? 'price__sale' : 'price__regular') + '">' +
      '<dd><span class="price-item price-item--' + (onSale ? 'sale' : 'regular') + ' elevate-ac__price">' +
      E.esc(selling) +
      '</span></dd>' +
      (onSale
        ? '<dd class="price__compare"><s class="price-item price-item--regular elevate-ac__price-was">' +
          E.esc(E.money(list.min)) +
          '</s></dd>'
        : '') +
      '</div>' +
      '</dl>' +
      '</div>'
    );
  }

  /**
   * Same rules as assets/elevate-card.js: both badge groups are rendered, and
   * Elevate's own DISCOUNT badge is skipped because the sale price is already
   * shown next to it. Keeping the two surfaces aligned matters as soon as a
   * custom badge is configured in Experience, since a badge that shows up on the
   * grid card but not in the modal reads as a bug in the theme.
   */
  function productBadge(product) {
    const groups = product.badges || {};
    // Member badge first, same rule and same order as the grid card.
    // No member badge here, unlike the grid card. product-card.liquid's own
    // "CG Medlem" badge is driven by a tag that matches nothing in the
    // catalogue (see the comment there), so the native card that replaces this
    // one never draws it - rendering it on the placeholder only makes a badge
    // appear and then vanish. The member *price* badge still arrives, from
    // assets/elevate-member-price.js, which is driven by a real price.
    const fromElevate = (groups.primary || [])
      .concat(groups.secondary || [])
      .filter((badge) => badge && badge.label && badge.theme !== 'DISCOUNT')
      .map(
        (badge) =>
          '<span class="badge badge--custom" data-badge-theme="' +
          E.esc(badge.theme || '') +
          '">' +
          E.esc(badge.label) +
          '</span>'
      )
      .join('');

    return fromElevate;
  }

  /**
   * Reserves the height of product-card.liquid's stock-status block, which
   * renders below .card-information__wrapper and is the only part of the native
   * card's height that is not already fixed (the image is square and
   * .collection-product-card .card-information__wrapper is a fixed 110px in
   * custom.css). Without it the placeholder is shorter than the card that
   * replaces it and every row below shifts down on upgrade.
   *
   * Two items with a non-breaking space, not an empty div: the height comes
   * from the same line boxes the real block has, so it tracks the theme rather
   * than a number measured once. Hidden with visibility, not display, so it
   * still occupies that space. Geometry is restated in
   * component-elevate-autocomplete.css because the real rules live in an inline
   * <style> inside product-card.liquid, which ships with a card and so is
   * absent on a page that has none.
   */
  const STOCK_RESERVE =
    '<div class="product-cart--stock-status elevate-ac__stock-reserve" aria-hidden="true">' +
    '<div class="stock-status-item"><span class="status-circle"></span>' +
    '<span class="product-cart--stock-status-text">&nbsp;</span></div>' +
    '<div class="stock-status-item"><span class="status-circle"></span>' +
    '<span class="product-cart--stock-status-text">&nbsp;</span></div>' +
    '</div>';

  /**
   * Deliberately a structural copy of snippets/product-card.liquid, down to the
   * <br> between brand and title, rather than markup of its own with matching
   * values. The panel's cards are replaced by that very snippet a moment later
   * (see open() and assets/elevate-native-cards.js), and reproducing the theme's
   * own class names is what makes the swap identical by construction - the
   * earlier hand-tuned version restated the theme's numbers and still drifted,
   * because .card__title-product's fixed two-line height and .card-information's
   * 1.6rem top padding are not things a separate set of rules lands on by
   * measurement.
   *
   * The trade this makes: the panel now depends on the theme's card CSS, so a
   * change to product-card.liquid's structure reaches the search modal too.
   *
   * The `elevate-ac__*` classes that remain are JS hooks, not styling -
   * elevate-member-price.js finds the price by `.elevate-ac__price`, and
   * elevate-native-cards.js finds the card and its link by
   * `.elevate-ac__product` / `.elevate-ac__product-link`.
   *
   * The root <li> matches sections/elevate-native-cards.liquid's own root, so
   * the replacement lands as the same kind of grid item. No outer <a> wrapping
   * the whole card: the theme's card holds three anchors of its own, and an <a>
   * around them would be reparsed and rearranged by the HTML parser. The
   * ticket sits on the root instead, where the delegated click handler's
   * closest() call finds it from anywhere in the card, and on the title link,
   * where upgradePlaceholder() reads it.
   */
  function productItem(group, index) {
    const product = (group.products && group.products[0]) || group;
    const ticket = group.ticket || product.ticket || '';
    const url = E.safeUrl(product.link);
    const variantKey = (product.variants && product.variants[0] && product.variants[0].key) || '';
    const badges = productBadge(product);
    return (
      '<li class="collection-product-card elevate-ac__product" role="option"' +
      ' id="elevate-ac-product-' + index + '"' +
      ' data-voyado-ticket="' + E.esc(ticket) + '"' +
      ' data-product-key="' + E.esc(product.key || '') + '"' +
      ' data-variant-key="' + E.esc(variantKey) + '">' +
      '<div class="card-wrapper">' +
      '<div class="card card--product">' +
      '<div class="card__inner full-unstyled-link">' +
      // padding-bottom inline, exactly as product-card.liquid sets it for
      // media_size 'square', and for the same reason: .card-wrapper .media
      // sets `padding: 0` and outranks .media--square, so the square has to
      // come from the style attribute or the image collapses to zero height.
      '<div class="media media--transparent media--square" style="padding-bottom: 100%;">' +
      productImage(product, index) +
      '</div>' +
      (badges ? '<div class="card__badge">' + badges + '</div>' : '') +
      '</div>' +
      '</div>' +
      '<div class="card-information">' +
      '<div class="card-information__wrapper">' +
      '<div class="card__title-block">' +
      '<h3 class="card__title h5">' +
      '<a class="full-unstyled-link elevate-ac__product-link" href="' + E.esc(url) + '"' +
      ' data-voyado-ticket="' + E.esc(ticket) + '">' +
      (product.brand ? '<span class="card__title-brand">' + E.esc(E.brandAsTitled(product)) + '</span><br>' : '') +
      '<span class="card__title-product">' + E.esc(E.titleWithoutBrand(product)) + '</span>' +
      '</a>' +
      '</h3>' +
      '</div>' +
      '<div class="card__view-details">' +
      '<div class="card__view-details-button">' +
      productPrice(product) +
      '</div>' +
      '</div>' +
      '</div>' +
      STOCK_RESERVE +
      '</div>' +
      // Matches the native card's own overlay link. Required, not cosmetic:
      // without the outer <a> the card had, the title text would otherwise be
      // the only clickable part of it.
      '<a href="' + E.esc(url) + '" class="link link--overlay card-wrapper__link--overlay"' +
      ' aria-label="' + E.esc(E.titleWithoutBrand(product)) + '"></a>' +
      '</div>' +
      // The last 24px of the native card's height, and the only part of it
      // that is not the theme's: product-card.liquid renders a Gift Reggie
      // form below .card-wrapper, and that app injects a
      // .giftreggie-pdp-cta-area into it carrying 12px of margin top and
      // bottom. Measured on the rendered card, where .card-wrapper is 405px
      // and the <li> around it 429px. Reserved rather than reproduced: the div
      // is the app's, and giving the placeholder the app's own class invites
      // its script to fill it in.
      '<div class="elevate-ac__giftreggie-reserve" aria-hidden="true"></div>' +
      '</li>'
    );
  }

  function sectionTitle(key, fallback, extraHtml) {
    return (
      '<div class="elevate-ac__section-head">' +
      '<span class="elevate-ac__section-title">' + E.esc(E.t(key, fallback)) + '</span>' +
      (extraHtml || '') +
      '</div>'
    );
  }

  /**
   * The heading is a parameter because the same list markup is used for search
   * suggestions and, in the empty state, for Elevate's popular phrases.
   */
  function renderPhrases(list, titleKey, titleFallback) {
    if (!list.length) return '';
    const items = list
      .map(
        (p, i) =>
          '<li role="option" id="elevate-ac-phrase-' + i + '">' +
          '<a class="elevate-ac__phrase-link" href="' + E.esc(searchUrl(p.q, 'PHRASE_SUGGEST')) + '" data-term="' + E.esc(p.q) + '">' +
          highlightPhrase(p) +
          '</a></li>'
      )
      .join('');
    return (
      '<div class="elevate-ac__section">' +
      sectionTitle(titleKey || 'suggestions', titleFallback || 'Suggestions') +
      '<ul class="elevate-ac__list" role="group">' + items + '</ul>' +
      '</div>'
    );
  }

  /**
   * Normalises the three shapes Elevate uses for products into one list of
   * groups that keeps each group's ticket:
   *
   *   productSuggestions          [{ ticket, products: [...] }]
   *   primaryList.productGroups   [{ key, remaining, products: [...] }]
   *   recentlyViewed              undocumented, so bare products are accepted too
   */
  function flattenGroups(list) {
    const groups = [];
    (list || []).forEach((entry) => {
      if (!entry) return;
      const products = entry.products || (entry.title ? [entry] : []);
      products.forEach((product) => {
        groups.push({ ticket: product.ticket || entry.ticket, products: [product] });
      });
    });
    return groups;
  }

  function renderProducts(groups, term, titleKey, titleFallback) {
    if (!groups.length) return '';
    const items = groups.map(productItem).join('');
    const seeAll = term
      ? '<a class="elevate-ac__see-all" href="' + E.esc(searchUrl(term, '')) + '">' +
        E.esc(E.t('view_all', 'View all')) + '</a>'
      : '';
    return (
      '<div class="elevate-ac__section elevate-ac__section--products">' +
      sectionTitle(titleKey || 'products', titleFallback || 'Products', seeAll) +
      '<ul class="elevate-ac__grid" role="group">' + items + '</ul>' +
      '</div>'
    );
  }

  /**
   * Content items are Shopify collections. Verified shape from the configured
   * `content` list (algorithm TOP_CONTENT):
   *
   *   { key: "collection-468282900720", type: "category",
   *     title: "Vannglass", link: "/collections/glass-vannglass",
   *     ticket: "...", custom: {} }
   *
   * Two consequences. There is no image field, so categories always render as
   * text. And the items carry a ticket, so clicks must be notified like product
   * clicks are.
   *
   * The same list also carries `type: "page"` items, mixed in with the
   * categories and ranked together by Elevate - both end up here, split
   * into the categories/pages sections below by `isPage`.
   *
   * The extractor stays tolerant of other shapes because a second list may be
   * configured later, and every section hides itself when empty.
   */
  function contentItems(data) {
    const raw = []
      .concat(data.contentSuggestions || [])
      .concat((data.contentLists || []).reduce((acc, list) => acc.concat(list.items || []), []));

    return raw
      .map((item) => {
        if (!item) return null;
        const title = item.title || item.label || item.name || item.q;
        const link = E.safeUrl(item.link || item.url);
        if (!title || !link) return null;
        const info = item.imageInfo || {};
        const image =
          E.safeUrl(
            info.thumbnail ||
              (info.images && info.images[0] && info.images[0].sources && info.images[0].sources[0] && info.images[0].sources[0].url) ||
              item.image
          ) || '';
        // `type` is not guaranteed. Anything that is not clearly a page is
        // treated as a category, because categories are the common case.
        const type = String(item.type || item.contentType || '').toUpperCase();
        return {
          title: title,
          link: link,
          image: image,
          ticket: item.ticket || '',
          isPage: type.indexOf('PAGE') !== -1 || type.indexOf('ARTICLE') !== -1
        };
      })
      .filter(Boolean);
  }

  function renderCategories(items) {
    if (!items.length) return '';
    const html = items
      .map(
        (item, i) =>
          '<li role="option" id="elevate-ac-cat-' + i + '">' +
          '<a class="elevate-ac__cat" href="' + E.esc(item.link) + '" data-voyado-ticket="' + E.esc(item.ticket) + '">' +
          (item.image
            ? '<img class="elevate-ac__cat-img" src="' + E.esc(item.image) + '" alt="" width="40" height="40" loading="lazy" decoding="async">'
            : '') +
          '<span class="elevate-ac__cat-label">' + E.esc(item.title) + '</span>' +
          '</a></li>'
      )
      .join('');
    return (
      '<div class="elevate-ac__section">' +
      sectionTitle('categories', 'Categories') +
      '<ul class="elevate-ac__list elevate-ac__list--cats" role="group">' + html + '</ul>' +
      '</div>'
    );
  }

  function renderPages(items) {
    if (!items.length) return '';
    const html = items
      .map(
        (item, i) =>
          '<li role="option" id="elevate-ac-page-' + i + '">' +
          '<a class="elevate-ac__page-link" href="' + E.esc(item.link) + '" data-voyado-ticket="' + E.esc(item.ticket) + '">' +
          '<span class="elevate-ac__page-label">' + E.esc(item.title) + '</span>' +
          '</a>' +
          '</li>'
      )
      .join('');
    return (
      '<div class="elevate-ac__section">' +
      sectionTitle('pages', 'Pages') +
      '<ul class="elevate-ac__list" role="group">' + html + '</ul>' +
      '</div>'
    );
  }

  function renderRecent(elevateList) {
    const list = recentTerms(elevateList);
    if (!list.length) return '';
    const items = list
      .map(
        (term, i) =>
          '<li role="option" id="elevate-ac-recent-' + i + '">' +
          '<a class="elevate-ac__phrase-link" href="' + E.esc(searchUrl(term, 'RECENT_SEARCH')) + '" data-term="' + E.esc(term) + '">' +
          E.esc(term) +
          '</a></li>'
      )
      .join('');
    return (
      '<div class="elevate-ac__section">' +
      sectionTitle('recent_searches', 'Recent searches') +
      '<ul class="elevate-ac__list" role="group">' + items + '</ul>' +
      '</div>'
    );
  }

  /**
   * Two-column layout: a narrow sidebar with phrases, categories and pages, and
   * a wide product grid. Whichever column would be empty is dropped and the
   * other spans the full width. Both variants keep their column wrapper, because
   * that wrapper carries the panel's padding and, for the products, the scroll
   * container.
   */
  function layout(sidebarHtml, mainHtml) {
    if (sidebarHtml && mainHtml) {
      const side = '<div class="elevate-ac__side">' + sidebarHtml + '</div>';
      const main = '<div class="elevate-ac__main">' + mainHtml + '</div>';
      // Same order on every breakpoint: suggestions before products, so reading
      // order and tab order match what is on screen without a viewport check.
      return '<div class="elevate-ac__layout">' + side + main + '</div>';
    }
    const single = sidebarHtml
      ? '<div class="elevate-ac__side">' + sidebarHtml + '</div>'
      : '<div class="elevate-ac__main">' + mainHtml + '</div>';
    return '<div class="elevate-ac__layout elevate-ac__layout--single">' + single + '</div>';
  }

  /* ---------------------------------------------------------------- controller */

  function createPanel(input) {
    const panel = document.createElement('div');
    panel.className = 'elevate-ac';
    panel.id = 'elevate-ac-' + input.id;
    panel.hidden = true;

    // The close button lives outside the content host, so rewriting results does
    // not remove it.
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'elevate-ac__close';
    closeBtn.setAttribute('aria-label', E.t('close', 'Close'));
    // The path is copied from snippets/icon-close.liquid, the same icon the cart
    // drawer uses, so the two close buttons are identical. A Liquid snippet
    // cannot be rendered from JS, hence the duplication.
    closeBtn.innerHTML =
      '<svg class="icon icon-close" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M7.00072 5.58599L11.9507 0.635986L13.3647 2.04999L8.41472 6.99999L13.3647 11.95L11.9507 13.364L7.00072 8.41399L2.05072 13.364L0.636719 11.95L5.58672 6.99999L0.636719 2.04999L2.05072 0.635986L7.00072 5.58599Z" fill="currentColor"/>' +
      '</svg>';

    const content = document.createElement('div');
    content.className = 'elevate-ac__content';
    content.setAttribute('role', 'listbox');
    content.setAttribute('aria-label', E.t('search', 'Search'));

    panel.appendChild(closeBtn);
    panel.appendChild(content);

    const status = document.createElement('span');
    status.className = 'elevate-ac__status visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    // Appended to body rather than next to the input on purpose. The panel is
    // position: fixed and spans from the search field to the bottom of the
    // viewport, and the sticky header applies a transform. A transformed ancestor
    // becomes the containing block for fixed positioning, which would break the
    // full-height layout, so the panel has to live outside the header.
    document.body.appendChild(panel);
    document.body.appendChild(status);

    return { panel: panel, content: content, closeBtn: closeBtn, status: status };
  }

  /**
   * Positions the panel directly below the search field. Recalculated on open,
   * resize and scroll, because the sticky header changes the field's position.
   */
  function positionPanel(input, panel) {
    const rect = input.getBoundingClientRect();
    panel.style.top = Math.max(0, Math.round(rect.bottom + 8)) + 'px';
  }

  function attach(input) {
    if (input.dataset.elevateAc === 'true') return;
    input.dataset.elevateAc = 'true';

    const refs = createPanel(input);
    const panel = refs.panel;
    const content = refs.content;
    const status = refs.status;

    // A11y is set here because the Liquid markup gates it behind a disabled
    // setting. See the file header.
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', panel.id);
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');

    let activeIndex = -1;
    let debounceTimer = null;

    // The close button moves focus back to the input for keyboard users. Without
    // this flag the focus handler would immediately reopen the panel on an empty
    // query, so the button appeared to do nothing.
    let skipNextFocusOpen = false;

    // focus and click both arrive from a single click on the field, and the
    // empty state is async, so the panel is still hidden when the click lands.
    // Without this the empty state would query Elevate twice per click.
    let emptyStatePending = false;

    function options() {
      return Array.from(panel.querySelectorAll('[role="option"]'));
    }

    function close() {
      // A hidden panel never intersects, so a surviving timer would fetch
      // nothing anyway - cleared so it cannot fire against content the visitor
      // has dismissed if the panel is reopened before it does.
      clearTimeout(upgradeTimer);
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      document.documentElement.classList.remove('elevate-ac-open');
      activeIndex = -1;
    }

    function open(html, announcement) {
      if (!html) {
        close();
        return;
      }
      content.innerHTML = html;
      // Swap the compact cards above for the same native product-card.liquid
      // the collection grid and the recommendation carousels render, so a
      // product looks identical wherever it appears - and gets member price,
      // SAMI badges and image ordering from Liquid rather than from the
      // Storefront API patch below.
      //
      // Lazily, not eagerly: a native card is one Liquid render per product,
      // so rendering the compact card first keeps the panel instant and the
      // upgrade arrives a moment later. Its own selectors rather than
      // elevate-card.js's, see upgradeLazily.
      //
      // Held back until the visitor stops typing. DEBOUNCE_MS gates the
      // Elevate query, not this: a query that resolves mid-typing still
      // renders, and every one of its cards used to be fetched and thrown away
      // a moment later. Measured on "glass" - 39 section fetches, of which 27
      // were for result sets ("g", "gl", "gla") that were never read.
      //
      // Gated on the same monotonic sequence the queries use rather than a
      // second staleness mechanism of its own, so a timer that fires after the
      // next render does nothing.
      scheduleUpgrade(content);
      // Still needed: it is what shows member price on a compact card whose
      // upgrade has not landed yet, or failed.
      if (window.CGElevateMemberPrice) window.CGElevateMemberPrice.enhance(content);
      positionPanel(input, panel);
      panel.hidden = false;
      document.documentElement.classList.add('elevate-ac-open');
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
      if (announcement) status.textContent = announcement;
    }

    function setActive(next) {
      const opts = options();
      if (!opts.length) return;
      opts.forEach((o) => o.classList.remove('is-active'));
      activeIndex = ((next % opts.length) + opts.length) % opts.length;
      const el = opts[activeIndex];
      el.classList.add('is-active');
      input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    }

    function build(data, term) {
      const phrases = data.phraseSuggestions || [];
      const productGroups = flattenGroups(data.productSuggestions);
      const entries = contentItems(data);
      const categories = entries.filter((c) => !c.isPage);
      const pages = entries.filter((c) => c.isPage);

      const sidebar = renderPhrases(phrases) + renderCategories(categories) + renderPages(pages);
      const main = renderProducts(productGroups, term);

      // Handled by renderNoHits, which needs to await more data.
      if (!sidebar && !main) return { empty: true, count: 0 };

      return { html: layout(sidebar, main), count: productGroups.length };
    }

    async function query(term) {
      const elevate = await E.whenReady();
      if (!elevate) return;

      const current = ++seq;
      try {
        // Content lists carry categories and pages. Requesting a list that is
        // not configured is harmless: the response comes back empty and the
        // sidebar sections hide themselves.
        const data = await elevate.query.autocomplete(
          { q: term, presentCustom: E.PRESENT_CUSTOM },
          { contentLists: contentListsConfig() }
        );
        if (current !== seq) return;
        const built = build(data || {}, term);
        if (built.empty) {
          await renderNoHits(elevate, term, current);
          return;
        }
        open(
          built.html,
          // A term can return phrases without products, and announcing
          // "0 products" for that would be wrong.
          built.count
            ? built.count + ' ' + E.t('products', 'Products').toLowerCase()
            : E.t('no_results', 'No results')
        );
      } catch (e) {
        if (current !== seq) return;
        close();
      }
    }

    /**
     * Products for the empty state.
     *
     * Which recommendation list, and which Elevate page to read it from, are
     * both merchandiser settings on sections/header.liquid - not decided
     * here. `/frontpage` is only the fallback when the Page ID setting is
     * left blank, matching this store's actual start-page reference
     * (confirmed in Experience under Category & Landing pages, not `/`).
     *
     * No algorithm and no `limit` in the request: an existing Experience-app
     * configuration for that list id takes precedence over one supplied
     * here, so this is really just naming which list to read from the
     * response. Elevate's own settings decide the rest.
     */
    async function startPageRecommendations(elevate) {
      const listId = autocompleteConfig().emptySearchListId;
      if (!listId) return { groups: [], entries: [] };

      const pageReference = E.normalizePageReference(autocompleteConfig().emptySearchPageId) || '/frontpage';

      try {
        const data = await elevate.query.landingPage(
          { pageReference: pageReference },
          {
            contentLists: contentListsConfig(),
            recommendationLists: [{ id: listId }]
          }
        );
        const lists = (data && data.recommendationLists) || [];
        const groups = lists.reduce((all, list) => all.concat(flattenGroups(list && list.productGroups)), []);
        return { groups: groups, entries: contentItems(data || {}) };
      } catch (e) {
        return { groups: [], entries: [] };
      }
    }

    /**
     * The shared content behind both the empty state and the no-hits state:
     * popular phrases, categories, and either the visitor's recently viewed
     * products or popular ones.
     *
     * Categories/pages always come from startPageRecommendations() - the
     * plain autocomplete endpoint has no page context, so its own
     * contentLists response is empty by design, regardless of whether the
     * visitor has recentlyViewed products. That call therefore always runs,
     * even when recentlyViewed already supplies the products, so entries
     * don't silently disappear for a returning visitor.
     *
     * `current` is passed in rather than claimed here, because the no-hits state
     * is a continuation of a query that already holds a sequence number.
     * Returns null when a newer request has started.
     */
    async function collectPopular(elevate, current) {
      const data =
        (await elevate.query.autocomplete({ q: '' }, { contentLists: contentListsConfig() })) || {};
      if (current !== seq) return null;

      const phrases = data.phraseSuggestions || [];
      let groups = flattenGroups(data.recentlyViewed);
      let titleKey = 'recently_viewed';
      let titleFallback = 'Recently viewed';

      // Elevate's own list needs a persisted customerKey (consent). A visitor
      // who declined still gets their own local history before falling all
      // the way through to the merchandiser's popular-products list.
      if (!groups.length) {
        groups = recentlyViewedProductGroups();
      }

      const recommended = await startPageRecommendations(elevate);
      if (current !== seq) return null;

      if (!groups.length && recommended.groups.length) {
        groups = recommended.groups;
        titleKey = 'popular';
        titleFallback = 'Popular';
      }

      return { phrases, entries: recommended.entries, groups, titleKey, titleFallback, recent: data.recentSearches };
    }

    function renderPopular(state, noticeHtml) {
      const sidebar =
        (noticeHtml || '') +
        renderRecent(state.recent) +
        renderPhrases(state.phrases, 'popular_searches', 'Popular searches') +
        renderCategories(state.entries.filter((c) => !c.isPage)) +
        renderPages(state.entries.filter((c) => c.isPage));
      const main = renderProducts(state.groups, '', state.titleKey, state.titleFallback);
      if (!sidebar && !main) return '';
      return layout(sidebar, main);
    }

    async function showEmptyState() {
      emptyStatePending = true;
      try {
        await renderEmptyState();
      } finally {
        emptyStatePending = false;
      }
    }

    /**
     * Shown when the field is focused with nothing typed. Everything here comes
     * from Elevate, including the counts, so merchandisers control it. The local
     * recent searches are only a fallback, see recentTerms().
     *
     * `recentlyViewed` and `recentSearches` are returned by the same endpoint but
     * stay empty until notifications have run for the session, which is why the
     * popular fallback exists.
     */
    async function renderEmptyState() {
      const localRecent = renderRecent();
      const elevate = await E.whenReady();
      if (!elevate) {
        // layout() always returns markup, so an empty panel would open here.
        if (localRecent) open(layout(localRecent, ''), '');
        else close();
        return;
      }

      const current = ++seq;
      try {
        const state = await collectPopular(elevate, current);
        if (!state) return;
        open(renderPopular(state, ''), '');
      } catch (e) {
        if (current !== seq) return;
        if (localRecent) open(layout(localRecent, ''), '');
        else close();
      }
    }

    /**
     * Corrections for a term Elevate could not match.
     *
     * Only `searchPage` carries `didYouMean` and `relatedSearches`; `autocomplete`
     * has neither. Both are empty in this catalogue today, because typo tolerance
     * resolves near misses before they ever reach a zero-hit state ("hadelnd"
     * returns 696 hits), so in practice this only fires on real gibberish, where
     * Elevate has nothing to suggest either. The rendering is here so the
     * suggestions appear by themselves if the engine starts returning them.
     *
     * RELATED_SEARCH is not a valid origin, so related searches are sent without
     * one and count as organic.
     */
    async function corrections(elevate, term) {
      try {
        // `limit: 1` is a bandwidth guard, not a display count: only didYouMean
        // and relatedSearches are read here, and the products are discarded.
        const data = await elevate.query.searchPage({ q: term, limit: 1, notify: false });
        const out = [];
        const add = (list, origin) => {
          (list || []).forEach((item) => {
            const q = typeof item === 'string' ? item : item && item.q;
            if (q) out.push({ q: q, origin: origin });
          });
        };
        add(data && data.didYouMean, 'DID_YOU_MEAN');
        add(data && data.relatedSearches, '');
        return out;
      } catch (e) {
        return [];
      }
    }

    function noHitsNotice(term, list) {
      const label = E.esc(E.t('no_results_for', 'No results for “__TERM__”')).replace(
        '__TERM__',
        () => E.esc(term)
      );
      let html = '<p class="elevate-ac__notice">' + label + '</p>';
      if (list.length) {
        html +=
          '<p class="elevate-ac__notice">' +
          E.esc(E.t('did_you_mean', 'Did you mean')) +
          ' ' +
          list
            .map(
              (item) =>
                '<a class="elevate-ac__notice-link" href="' +
                E.esc(searchUrl(item.q, item.origin)) +
                '" data-term="' +
                E.esc(item.q) +
                '">' +
                E.esc(item.q) +
                '</a>'
            )
            .join(', ') +
          '</p>';
      }
      return '<div class="elevate-ac__section elevate-ac__section--notice">' + html + '</div>';
    }

    /**
     * Zero hits. Elevate returns nothing at all for such a term, not even
     * phrases, so the panel shows the same popular content as the empty state
     * with a notice on top. Without this the modal was a single line of text on
     * an otherwise blank full-height panel.
     */
    async function renderNoHits(elevate, term, current) {
      const [list, state] = await Promise.all([
        corrections(elevate, term),
        collectPopular(elevate, current)
      ]);
      if (current !== seq) return;
      const notice = noHitsNotice(term, list);
      const html = state ? renderPopular(state, notice) : layout(notice, '');
      open(html, E.t('no_results', 'No results'));
    }

    input.addEventListener('input', () => {
      // trimStart, not trim. Elevate distinguishes "glass " (complete word, next
      // term) from "glass" (prefix completion), so trailing space must survive.
      const term = input.value.trimStart();
      clearTimeout(debounceTimer);
      if (!term.trim()) {
        debounceTimer = setTimeout(showEmptyState, DEBOUNCE_MS);
        return;
      }
      debounceTimer = setTimeout(() => query(term), DEBOUNCE_MS);
    });

    input.addEventListener('focus', () => {
      if (skipNextFocusOpen) {
        skipNextFocusOpen = false;
        return;
      }
      if (!input.value.trim()) showEmptyState();
    });

    // Reopens the panel when the field is clicked after a dismissal. Focus alone
    // cannot do it: clicking an already focused input fires no focus event.
    input.addEventListener('click', () => {
      if (panel.hidden && !emptyStatePending && !input.value.trim()) showEmptyState();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (panel.hidden) showEmptyState();
        else setActive(activeIndex + 1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndex - 1);
        return;
      }
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key === 'Enter') {
        const opts = options();
        if (activeIndex >= 0 && opts[activeIndex]) {
          const link = opts[activeIndex].querySelector('a');
          if (link) {
            event.preventDefault();
            link.click();
            return;
          }
        }
        pushRecent(input.value);
      }
    });

    refs.closeBtn.addEventListener('click', () => {
      close();
      // Focus goes back to the field, which is the panel's trigger. The flag
      // stops that focus from reopening what was just closed, and is only set
      // when focus actually moves: Safari does not focus a clicked button, so
      // setting it unconditionally would leave it stale and swallow a later
      // genuine focus.
      skipNextFocusOpen = document.activeElement !== input;
      input.focus();
    });

    // Delegated click, so it survives the content host being rewritten.
    content.addEventListener('click', (event) => {
      const phrase = event.target.closest('[data-term]');
      if (phrase) {
        pushRecent(phrase.dataset.term);
        return;
      }
      const product = event.target.closest('[data-voyado-ticket]');
      if (product) {
        pushRecent(input.value);
        E.notifyClick(product.dataset.voyadoTicket);
      }
    });

    document.addEventListener('click', (event) => {
      if (!panel.contains(event.target) && event.target !== input) close();
    });

    // The sticky header moves the field, so the panel has to follow it.
    const reposition = () => {
      if (!panel.hidden) positionPanel(input, panel);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, { passive: true });

    const form = input.closest('form');
    if (form) form.addEventListener('submit', () => pushRecent(input.value));
  }

  function init() {
    INPUT_IDS.forEach((id) => {
      document.querySelectorAll('input#' + id).forEach(attach);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
